import {
  CheckCircle as ReadyIcon,
  Close as CloseIcon,
  ErrorOutline as InvalidIcon,
  UploadFile as UploadIcon,
  WarningAmber as DuplicateIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import type { Contact } from '@otw/models/leads/contacts'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import type { PrivateUserInfoType } from '@otw/models'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  coerceContactOwnerId,
  contactsCsvRowHasFollowUpWork,
  matchContactOwnerFromCsv,
  revalidateContactsCsvRow,
  type ParsedContactsCsvRow,
} from '@/core/contacts/parseContactsCsv'
import {
  formatContactDisplayName,
  normalizeContactEmail,
} from '@/core/contacts/contactIdentity'
import type { CreateContactInput, UpdateContactInput } from 'hooks/contacts/useContacts'
import type {
  AddContactCompanyMemberInput,
  CreateContactCompanyInput,
} from 'hooks/contacts/useContactCompanies'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { salesReps } from '@/constants'
import { formatLastActivityDateTime } from '../../util/activityDisplay'

export interface ImportContactsCsvDialogProps {
  open: boolean
  onClose: () => void
  rows: ParsedContactsCsvRow[]
  contacts: Contact[]
  users: PrivateUserInfoType[]
  companies: ContactCompany[]
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
  /** Shared timestamp applied as `lastActivityAt` when a row has no parsed date. */
  uploadTimestamp: Date
  createContact: (input: CreateContactInput) => Promise<string>
  updateContact: (input: UpdateContactInput) => Promise<unknown>
  createCompany: (input: CreateContactCompanyInput) => Promise<string>
  addCompanyMember: (input: AddContactCompanyMemberInput) => Promise<unknown>
}

const statusChip = (status: ParsedContactsCsvRow['status']) => {
  switch (status) {
    case 'ready':
      return (
        <Chip size="small" color="success" icon={<ReadyIcon />} label="Ready" />
      )
    case 'duplicate':
      return (
        <Chip
          size="small"
          color="warning"
          icon={<DuplicateIcon />}
          label="Duplicate"
        />
      )
    case 'invalid':
      return (
        <Chip
          size="small"
          color="error"
          icon={<InvalidIcon />}
          label="Invalid"
        />
      )
  }
}

const normalizeCompanyNameKey = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ')

const ImportContactsCsvDialog: React.FC<ImportContactsCsvDialogProps> = ({
  open,
  onClose,
  rows: initialRows,
  contacts,
  users,
  companies,
  salesOwners,
  inventoryOwners,
  uploadTimestamp,
  createContact,
  updateContact,
  createCompany,
  addCompanyMember,
}) => {
  const { showSuccess, showError } = useSnackbar()
  const [rows, setRows] = useState<ParsedContactsCsvRow[]>(initialRows)
  const [isCreating, setIsCreating] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  useEffect(() => {
    if (!open) return
    setRows(initialRows)
    setIsCreating(false)
    setProgress({ done: 0, total: 0 })
  }, [open, initialRows])

  const counts = useMemo(() => {
    const ready = rows.filter((r) => r.status === 'ready').length
    const duplicate = rows.filter((r) => r.status === 'duplicate').length
    const invalid = rows.filter((r) => r.status === 'invalid').length
    const duplicateFollowUp = rows.filter(
      (r) => r.status === 'duplicate' && contactsCsvRowHasFollowUpWork(r),
    ).length
    return { ready, duplicate, invalid, duplicateFollowUp, total: rows.length }
  }, [rows])

  const actionableRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.status === 'ready' ||
          (r.status === 'duplicate' && contactsCsvRowHasFollowUpWork(r)),
      ),
    [rows],
  )

  const revalidateAll = useCallback(
    (nextRows: ParsedContactsCsvRow[]) =>
      nextRows.map((row) =>
        revalidateContactsCsvRow(
          row,
          contacts,
          users,
          nextRows.filter((s) => s.rowNumber !== row.rowNumber),
        ),
      ),
    [contacts, users],
  )

  const updateRowField = useCallback(
    (rowNumber: number, field: 'name' | 'phone' | 'email', value: string) => {
      setRows((prev) => {
        const updated = prev.map((row) =>
          row.rowNumber === rowNumber ? { ...row, [field]: value } : row,
        )
        return revalidateAll(updated)
      })
    },
    [revalidateAll],
  )

  const resolveOrCreateCompanyId = async (
    companyName: string,
    companyIdByName: Map<string, string>,
  ): Promise<string> => {
    const key = normalizeCompanyNameKey(companyName)
    const existing = companyIdByName.get(key)
    if (existing) return existing
    const companyId = await createCompany({
      name: companyName,
      ownerId: null,
    })
    companyIdByName.set(key, companyId)
    return companyId
  }

  const linkContactToCompany = async (
    contactId: string,
    companyName: string,
    companyIdByName: Map<string, string>,
  ) => {
    const trimmed = companyName.trim()
    if (!trimmed || !contactId) return
    const companyId = await resolveOrCreateCompanyId(trimmed, companyIdByName)
    await addCompanyMember({
      companyId,
      contactId,
      role: 'manager',
    })
  }

  const resolveRowOwner = (row: ParsedContactsCsvRow) => {
    const rematched = matchContactOwnerFromCsv(
      row.contactOwner ?? '',
      inventoryOwners,
      salesOwners,
      salesReps,
    )
    if (rematched) return rematched
    if (row.matchedOwnerId !== undefined && row.matchedOwnerKind) {
      return {
        id: coerceContactOwnerId(row.matchedOwnerId),
        kind: row.matchedOwnerKind,
      }
    }
    return null
  }

  const applyOwnerToExistingContact = async (
    contact: Contact,
    owner: { id: string | number; kind: 'inventory' | 'sales' },
  ) => {
    const ownerId =
      owner.kind === 'sales' ? coerceContactOwnerId(owner.id) : (contact.ownerId ?? null)
    const inventoryOwnerId =
      owner.kind === 'inventory'
        ? coerceContactOwnerId(owner.id)
        : (contact.inventoryOwnerId ?? null)

    const alreadySet =
      owner.kind === 'sales'
        ? String(contact.ownerId ?? '') === String(owner.id)
        : String(contact.inventoryOwnerId ?? '') === String(owner.id)
    if (alreadySet) return

    await updateContact({
      id: contact.id,
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      birthday: contact.birthday,
      ownerId,
      inventoryOwnerId,
      stage: contact.stage,
      inventoryStage: contact.inventoryStage ?? 'prospect',
      dropoffLocation: contact.dropoffLocation,
      leadIds: contact.leadIds,
    })
  }

  const handleConfirm = async () => {
    if (actionableRows.length === 0) return

    setIsCreating(true)
    setProgress({ done: 0, total: actionableRows.length })

    let created = 0
    let updated = 0
    let failed = 0
    const companyIdByName = new Map<string, string>()
    for (const company of companies) {
      const key = normalizeCompanyNameKey(company.name)
      if (key && !companyIdByName.has(key)) {
        companyIdByName.set(key, company.id)
      }
    }

    for (const row of actionableRows) {
      try {
        const resolvedOwner = resolveRowOwner(row)

        if (row.status === 'ready') {
          const rawName = row.name.trim()
          const email = row.email.trim()
          const nameIsEmail =
            Boolean(email) &&
            normalizeContactEmail(rawName) === normalizeContactEmail(email)
          const contactId = await createContact({
            name: nameIsEmail ? rawName : formatContactDisplayName(rawName) || rawName,
            ...(email ? { email } : {}),
            ...(row.phone.trim() ? { phone: row.phone.trim() } : {}),
            ownerId:
              resolvedOwner?.kind === 'sales'
                ? coerceContactOwnerId(resolvedOwner.id)
                : null,
            inventoryOwnerId:
              resolvedOwner?.kind === 'inventory'
                ? coerceContactOwnerId(resolvedOwner.id)
                : null,
            stage: 'prospect',
            inventoryStage: 'prospect',
            lastActivityAt: row.lastActivityAt ?? uploadTimestamp,
            ...(row.matchedUserId ? { userId: row.matchedUserId } : {}),
          })

          await linkContactToCompany(
            contactId,
            row.associatedCompany ?? '',
            companyIdByName,
          )
          created += 1
        } else if (row.status === 'duplicate' && row.matchedContactId) {
          const existing = contacts.find((c) => c.id === row.matchedContactId)
          if (!existing) {
            failed += 1
          } else {
            await linkContactToCompany(
              existing.id,
              row.associatedCompany ?? '',
              companyIdByName,
            )
            if (resolvedOwner) {
              await applyOwnerToExistingContact(existing, resolvedOwner)
            }
            updated += 1
          }
        }
      } catch {
        failed += 1
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }))
    }

    setIsCreating(false)

    const skipped =
      counts.invalid + (counts.duplicate - counts.duplicateFollowUp)
    const parts: string[] = []
    if (created > 0) {
      parts.push(`Created ${created} contact${created === 1 ? '' : 's'}`)
    }
    if (updated > 0) {
      parts.push(
        `updated ${updated} existing contact${updated === 1 ? '' : 's'}`,
      )
    }
    if (failed > 0) parts.push(`${failed} failed`)
    if (skipped > 0) parts.push(`${skipped} skipped`)

    const summary = parts.join('; ') || 'Nothing to import.'
    if (failed > 0) showError(summary)
    else showSuccess(summary)
    onClose()
  }

  const columns: GridColDef<ParsedContactsCsvRow>[] = useMemo(
    () => [
      {
        field: 'rowNumber',
        headerName: 'Row',
        width: 70,
        sortable: false,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.2,
        minWidth: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ParsedContactsCsvRow>) => {
          const editable = params.row.status === 'ready' || params.row.status === 'invalid'
          if (!editable || isCreating) {
            return params.row.name || '—'
          }
          return (
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={params.row.name}
              onChange={(e) => updateRowField(params.row.rowNumber, 'name', e.target.value)}
              onBlur={() => {
                const current = params.row.name.trim()
                const email = params.row.email.trim()
                const nameIsEmail =
                  Boolean(email) &&
                  normalizeContactEmail(current) === normalizeContactEmail(email)
                updateRowField(
                  params.row.rowNumber,
                  'name',
                  nameIsEmail ? current : formatContactDisplayName(current),
                )
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )
        },
      },
      {
        field: 'phone',
        headerName: 'Number',
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ParsedContactsCsvRow>) => {
          const editable = params.row.status === 'ready' || params.row.status === 'invalid'
          if (!editable || isCreating) {
            return params.row.phone || '—'
          }
          return (
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={params.row.phone}
              onChange={(e) => updateRowField(params.row.rowNumber, 'phone', e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          )
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ParsedContactsCsvRow>) => {
          const editable = params.row.status === 'ready' || params.row.status === 'invalid'
          if (!editable || isCreating) {
            return params.row.email || '—'
          }
          return (
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={params.row.email}
              onChange={(e) => updateRowField(params.row.rowNumber, 'email', e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          )
        },
      },
      {
        field: 'lastActivityAt',
        headerName: 'Last activity',
        flex: 0.9,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatLastActivityDateTime(params.row.lastActivityAt)}
          </Typography>
        ),
      },
      {
        field: 'associatedCompany',
        headerName: 'Company',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.row.associatedCompany?.trim() || '—'}
          </Typography>
        ),
      },
      {
        field: 'contactOwner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          const raw = params.row.contactOwner?.trim() || ''
          if (!raw) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )
          }
          if (params.row.matchedOwnerKind === 'inventory') {
            return (
              <Typography variant="body2" noWrap>
                {raw} (inventory)
              </Typography>
            )
          }
          if (params.row.matchedOwnerKind === 'sales') {
            return (
              <Typography variant="body2" noWrap>
                {raw} (sales)
              </Typography>
            )
          }
          return (
            <Typography variant="body2" color="warning.main" noWrap>
              {raw} (unmatched)
            </Typography>
          )
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        sortable: false,
        renderCell: (params) => statusChip(params.row.status),
      },
      {
        field: 'skipReason',
        headerName: 'Details',
        flex: 1.1,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          const notes: string[] = []
          if (params.row.skipReason) notes.push(params.row.skipReason)
          if (params.row.matchedUserId && params.row.status === 'ready') {
            notes.push('Portal user linked')
          }
          if (
            params.row.associatedCompany?.trim() &&
            (params.row.status === 'ready' || params.row.status === 'duplicate')
          ) {
            notes.push(
              params.row.status === 'duplicate'
                ? 'Will link company to existing'
                : 'Will link company',
            )
          }
          if (
            params.row.matchedOwnerId &&
            (params.row.status === 'ready' || params.row.status === 'duplicate')
          ) {
            notes.push(
              params.row.matchedOwnerKind === 'inventory'
                ? 'Inventory owner'
                : 'Sales owner',
            )
          }
          if (
            params.row.contactOwner?.trim() &&
            !params.row.matchedOwnerId &&
            (params.row.status === 'ready' || params.row.status === 'duplicate')
          ) {
            notes.push('Owner not matched')
          }
          return (
            <Typography variant="caption" color="text.secondary" noWrap>
              {notes.join(' · ') || '—'}
            </Typography>
          )
        },
      },
    ],
    [isCreating, updateRowField],
  )

  const progressPct =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0

  const confirmLabel = (() => {
    if (isCreating) {
      return `Importing… (${progress.done}/${progress.total})`
    }
    const parts: string[] = []
    if (counts.ready > 0) {
      parts.push(
        `Create ${counts.ready} contact${counts.ready === 1 ? '' : 's'}`,
      )
    }
    if (counts.duplicateFollowUp > 0) {
      parts.push(
        `update ${counts.duplicateFollowUp} existing`,
      )
    }
    return parts.join(' · ') || 'Import'
  })()

  return (
    <Dialog open={open} onClose={isCreating ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6, position: 'relative' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <UploadIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight={700}>
            Import contacts from CSV
          </Typography>
        </Stack>
        {!isCreating ? (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip color="success" label={`${counts.ready} ready`} size="small" />
            <Chip color="warning" label={`${counts.duplicate} duplicate`} size="small" />
            {counts.duplicateFollowUp > 0 ? (
              <Chip
                color="info"
                label={`${counts.duplicateFollowUp} duplicate with updates`}
                size="small"
              />
            ) : null}
            <Chip color="error" label={`${counts.invalid} invalid`} size="small" />
            <Chip variant="outlined" label={`${counts.total} total`} size="small" />
          </Stack>

          {actionableRows.length === 0 && counts.total > 0 ? (
            <Alert severity="warning">
              No rows are ready to import. Fix invalid rows or choose a different file.
            </Alert>
          ) : null}

          {isCreating ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Importing… {progress.done} / {progress.total}
              </Typography>
              <LinearProgress variant="determinate" value={progressPct} />
            </Box>
          ) : null}

          <Box sx={{ height: 420, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.rowNumber}
              disableRowSelectionOnClick
              hideFooterSelectedRowCount
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: 0,
                '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isCreating || actionableRows.length === 0}
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportContactsCsvDialog
